import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import DashboardCard from '../../../shared/ui/cards/DashboardCard';
import { StatusActionButton, Timeline, TimelineSegment, TimelineHandle } from '../../../shared/ui';
import { sessionsV2Api, SessionV2, StartMySessionV2Request, EndMySessionV2Request, CreateSessionActivityRequest, SessionActivityUpdateRequest, UpdateSessionV2Request, UpdateSessionActivityRequest } from '../../../api/sessionsV2Api';
import { sessionApi } from '../../../api/sessionApi';
import { typesOfWorkV2Api, TypeOfWorkV2 } from '../../../api/typesOfWorkV2Api';
import { contractsV2Api, ContractV2 } from '../../../api/contractsV2Api';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useOptimizedSignalR, SessionEvent } from '../../../hooks/useOptimizedSignalR';
import { useToast } from '../../../shared/contexts/ToastContext';
import { compressImageFiles } from '../../../utils/imageCompression';
import { openFileInput } from '../../../utils/filePicker';
import BottomSheet from '../../../shared/ui/forms/BottomSheet';
import WorkTypeSelector from '../../../shared/ui/forms/WorkTypeSelector';
import HorizontalCategoryTabs from '../../../shared/ui/HorizontalCategoryTabs';
import '../../../shared/ui/forms/WorkTypeSelector.css';
import '../../siteShifts/ui/CreateSessionV2Modal.css';
import './MyShiftCardV2.css';
import styles from './MyShiftCardV2.module.css';

interface ActivityItem {
  id: number | string;
  typeOfWorkId: number;
  typeOfWorkName?: string;
  contractId?: number;
  contractTitle?: string;
  timeStart: Date;
  rate: number;
  hours: number;
  percentage: number;
  amount: number;
  isNew: boolean;
  isModified: boolean;
  comment?: string;
  additionalWorkReason?: string;
  isAdditionalWork?: boolean;
  isHoursOnly?: boolean;
}

// Мемоизированный компонент для элемента активности (скопировано из CreateSessionV2Modal)
const ActivityItemComponent = React.memo<{
  item: ActivityItem;
  index: number;
  activities: ActivityItem[];
  sessionEnd: Date;
  formatTime: (date: Date) => string;
  formatDuration: (hours: number) => { hours: number; minutes: number };
  getActivityEndTime: (items: ActivityItem[], index: number, sessionEndTime: Date) => Date;
  updateActivity: (id: number | string, updates: Partial<ActivityItem>) => void;
  handleDeleteActivity: (id: number | string) => void;
  onCommentClick: (item: ActivityItem) => void;
  onAdditionalWorkClick: (item: ActivityItem) => void;
  onActivityTypeClick: (item: ActivityItem) => void;
  isLast: boolean;
}>(({
  item,
  index,
  activities,
  sessionEnd,
  formatTime,
  formatDuration,
  getActivityEndTime,
  updateActivity,
  handleDeleteActivity,
  onCommentClick,
  onAdditionalWorkClick,
  onActivityTypeClick,
  isLast
}) => {
  const endTime = getActivityEndTime(activities, index, sessionEnd);
  const nextActivity = index < activities.length - 1 ? activities[index + 1] : null;

  return (
    <React.Fragment>
      <div className="activity-item">
        <div className="activity-row activity-row-1">
          <div className="activity-col-time-block">
            <div className="activity-time-block">
              <div className="activity-time-block-duration">
                <span>{formatDuration(item.hours).hours} ч</span>
                {formatDuration(item.hours).minutes > 0 && (
                  <span> {formatDuration(item.hours).minutes} м</span>
                )}
              </div>
              <div className="activity-time-block-percent">
                <span className="percent-value">{item.percentage.toFixed(1)}%</span>
              </div>
            </div>
          </div>
          <div className="activity-vertical-separator"></div>
          <div className="activity-col-type-rate-amount">
            {item.contractId ? (
              <button
                type="button"
                className="activity-contract-display activity-contract-clickable"
                onClick={() => onActivityTypeClick(item)}
                title="Нажмите, чтобы изменить контракт или переключить на вид работ"
              >
                <span className="activity-contract-badge">Сделка</span>
                <span className="activity-contract-title">{item.contractTitle || `Контракт #${item.contractId}`}</span>
                <i className="ri-arrow-right-s-line activity-contract-edit-icon"></i>
              </button>
            ) : (
              <div
                className="activity-worktype-wrapper"
                onClickCapture={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onActivityTypeClick(item);
                }}
              >
                <WorkTypeSelector
                  value={item.typeOfWorkId}
                  onChange={(value) => value && updateActivity(item.id, { typeOfWorkId: value, contractId: undefined, contractTitle: undefined })}
                  placeholder="Выбрать"
                  compact
                />
              </div>
            )}
            <div className="activity-col-rate-amount">
              {item.contractId ? (
                <div className="activity-col-amount">
                  <label>Сумма</label>
                  <span className="amount-value" style={{ fontStyle: 'italic' }}>По контракту</span>
                </div>
              ) : (
                <>
                  <div className="activity-col-rate">
                    <label>Ставка</label>
                    <span style={{ padding: '8px', backgroundColor: '#2a2a2a', color: '#888', borderRadius: '4px', display: 'block' }}>
                      {item.rate > 0 ? `${item.rate.toLocaleString('ru-RU')} ₽/ч` : '—'}
                    </span>
                  </div>
                  <div className="activity-col-amount">
                    <label>Сумма</label>
                    <span className="amount-value">
                      {item.rate > 0 ? `${item.amount.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽` : '—'}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="activity-col-actions" style={{ flexDirection: 'column', gap: '8px' }}>
            <button
              type="button"
              className="btn-comment"
              onClick={() => onCommentClick(item)}
              title={item.comment ? 'Редактировать комментарий' : 'Добавить комментарий'}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', padding: 0,
                background: item.comment ? 'rgba(74, 158, 255, 0.15)' : 'transparent',
                color: item.comment ? '#4a9eff' : '#888',
                border: item.comment ? '1.5px solid rgba(74, 158, 255, 0.3)' : '1.5px solid rgba(136, 136, 136, 0.3)',
                borderRadius: '6px', cursor: 'pointer', fontSize: '14px', transition: 'all 0.2s ease'
              }}
            >
              <i className={item.comment ? 'ri-chat-3-fill' : 'ri-chat-3-line'}></i>
            </button>
            <button
              type="button"
              className="btn-additional-work"
              onClick={() => onAdditionalWorkClick(item)}
              title={item.isAdditionalWork ? 'Доп.работа (нажмите чтобы снять)' : 'Пометить как доп.работу'}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', padding: 0, flexShrink: 0,
                background: item.isAdditionalWork ? 'rgba(255, 170, 0, 0.15)' : 'transparent',
                color: item.isAdditionalWork ? '#ffaa00' : '#888',
                border: item.isAdditionalWork ? '1.5px solid rgba(255, 170, 0, 0.3)' : '1.5px solid rgba(136, 136, 136, 0.3)',
                borderRadius: '6px', cursor: 'pointer', fontSize: '14px', transition: 'all 0.2s ease'
              }}
            >
              <i className={item.isAdditionalWork ? 'ri-star-fill' : 'ri-star-line'}></i>
            </button>
            <button
              type="button"
              className="btn-delete"
              onClick={() => handleDeleteActivity(item.id)}
              title="Удалить"
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          </div>
        </div>
      </div>
      {isLast ? (
        <div className="activity-separator">
          <div className="activity-separator-line"></div>
          <div className="activity-separator-time">
            <span title="Конец смены" className="activity-start-time-label">
              {formatTime(endTime)}
            </span>
          </div>
        </div>
      ) : nextActivity ? (
        <div className="activity-separator">
          <div className="activity-separator-line"></div>
          <div className="activity-separator-time">
            <input
              type="time"
              value={`${String(nextActivity.timeStart.getHours()).padStart(2, '0')}:${String(nextActivity.timeStart.getMinutes()).padStart(2, '0')}`}
              onChange={(e) => {
                const [h, m] = e.target.value.split(':');
                const newTime = new Date(nextActivity.timeStart);
                newTime.setHours(parseInt(h), parseInt(m));
                updateActivity(nextActivity.id, { timeStart: newTime });
              }}
              className="activity-time-picker-input"
              id={`time-input-${nextActivity.id}`}
            />
          </div>
        </div>
      ) : null}
    </React.Fragment>
  );
}, (prevProps, nextProps) => {
  if (prevProps.item.id !== nextProps.item.id) return false;
  if (prevProps.isLast !== nextProps.isLast) return false;
  if (prevProps.index !== nextProps.index) return false;
  
  const prevHash = `${prevProps.item.timeStart.getTime()}-${prevProps.item.typeOfWorkId}-${prevProps.item.rate}-${prevProps.item.hours}-${prevProps.item.comment ?? ''}-${prevProps.item.isAdditionalWork ?? false}`;
  const nextHash = `${nextProps.item.timeStart.getTime()}-${nextProps.item.typeOfWorkId}-${nextProps.item.rate}-${nextProps.item.hours}-${nextProps.item.comment ?? ''}-${nextProps.item.isAdditionalWork ?? false}`;
  
  return prevHash === nextHash && prevProps.activities.length === nextProps.activities.length;
});

ActivityItemComponent.displayName = 'ActivityItemComponent';

interface MyShiftCardV2Props {
  siteId: number;
}

const MyShiftCardV2: React.FC<MyShiftCardV2Props> = ({ siteId }) => {
  const [activeSession, setActiveSession] = useState<SessionV2 | null>(null);
  const [closedSessionsWithoutActivities, setClosedSessionsWithoutActivities] = useState<SessionV2[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Активности для открытой сессии
  const [activeSessionActivities, setActiveSessionActivities] = useState<any[]>([]);
  const [loadingActiveSessionActivities, setLoadingActiveSessionActivities] = useState(false);
  const [elapsedTime, setElapsedTime] = useState<string>('');
  const [earnedAmount, setEarnedAmount] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Состояния для начала смены
  const [isStarting, setIsStarting] = useState(false);
  const [showConfirmStartShift, setShowConfirmStartShift] = useState(false);
  
  // Состояния для закрытия смены
  const [isEnding, setIsEnding] = useState(false);
  const [showConfirmEndShift, setShowConfirmEndShift] = useState(false);
  
  // Состояния для добавления контрольной точки
  const [isAddingCheckpoint, setIsAddingCheckpoint] = useState(false);
  const [showConfirmAddCheckpoint, setShowConfirmAddCheckpoint] = useState(false);
  
  // Состояния для добавления активности с длительностью
  const [showAddActivitySheet, setShowAddActivitySheet] = useState(false);
  const [activityHours, setActivityHours] = useState<number>(1);
  const [activityMinutes, setActivityMinutes] = useState<number>(0);
  const [isAddingActivity, setIsAddingActivity] = useState(false);
  
  // Состояния для подтверждения удаления пересекающихся активностей
  const [showConfirmDeleteSheet, setShowConfirmDeleteSheet] = useState(false);
  const [activitiesToDelete, setActivitiesToDelete] = useState<any[]>([]);
  const [pendingActivityData, setPendingActivityData] = useState<{startTime: Date, endTime: Date} | null>(null);
  
  // Состояния для bottom sheet заполнения активностей закрытой смены
  const [showActivitiesSheet, setShowActivitiesSheet] = useState(false);
  const [sessionForActivities, setSessionForActivities] = useState<SessionV2 | null>(null);
  const [timeEnd, setTimeEnd] = useState<string>('');
  
  // Состояния для активностей
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  
  // Состояния для временной шкалы
  const [draggingHandleIndex, setDraggingHandleIndex] = useState<number | null>(null);
  const [timelineRef, setTimelineRef] = useState<HTMLDivElement | null>(null);
  const [tempTimelinePositions, setTempTimelinePositions] = useState<Map<number, Date> | null>(null);
  
  // Состояния для модального окна выбора активностей (Ставка | Сделка)
  const [showWorkTypeSelector, setShowWorkTypeSelector] = useState(false);
  const [activitySourceTab, setActivitySourceTab] = useState<'ставка' | 'сделка'>('ставка');
  const [selectedWorkTypeIds, setSelectedWorkTypeIds] = useState<number[]>([]);
  const [selectedContractIds, setSelectedContractIds] = useState<number[]>([]);
  const [editingActivityId, setEditingActivityId] = useState<number | string | null>(null);
  const [typesOfWork, setTypesOfWork] = useState<TypeOfWorkV2[]>([]);
  const [contracts, setContracts] = useState<ContractV2[]>([]);
  const [loadingWorkTypes, setLoadingWorkTypes] = useState(false);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [workTypeSearchQuery, setWorkTypeSearchQuery] = useState('');
  const [contractSearchQuery, setContractSearchQuery] = useState('');
  const [selectedWorkTypeCategory, setSelectedWorkTypeCategory] = useState<string | null>(null);
  
  // Состояния для комментария и доп.работы в окне «Указать виды работ»
  const [showCommentSheet, setShowCommentSheet] = useState(false);
  const [currentActivityForComment, setCurrentActivityForComment] = useState<ActivityItem | null>(null);
  const [activityComment, setActivityComment] = useState('');
  const [showAdditionalWorkSheet, setShowAdditionalWorkSheet] = useState(false);
  const [currentActivityForAdditionalWork, setCurrentActivityForAdditionalWork] = useState<ActivityItem | null>(null);
  const [additionalWorkReason, setAdditionalWorkReason] = useState('');
  const [commentPhotos, setCommentPhotos] = useState<File[]>([]);
  const [commentPhotoPreviews, setCommentPhotoPreviews] = useState<string[]>([]);
  const [additionalWorkPhotos, setAdditionalWorkPhotos] = useState<File[]>([]);
  const [additionalWorkPhotoPreviews, setAdditionalWorkPhotoPreviews] = useState<string[]>([]);
  const commentFileInputRef = useRef<HTMLInputElement>(null);
  const additionalWorkFileInputRef = useRef<HTMLInputElement>(null);
  const commentPhotoUrlsRef = useRef<Set<string>>(new Set());
  const additionalWorkPhotoUrlsRef = useRef<Set<string>>(new Set());
  
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();

  // Загрузка активной смены и закрытых смен без активностей
  const loadActiveSession = useCallback(async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      // Загружаем активную смену
      const session = await sessionsV2Api.getMyActiveSession(siteId);
      setActiveSession(session);
      
      // Если есть активная смена, загружаем её активности (включая "Не указано")
      if (session) {
        setLoadingActiveSessionActivities(true);
        try {
          const activities = await sessionsV2Api.getSessionActivities(siteId, session.id);
          // Загружаем все активности, включая "Не указано" (TypeOfWorkId <= 1)
          setActiveSessionActivities(activities);
        } catch (error) {
          console.error('Ошибка загрузки активностей открытой смены:', error);
          setActiveSessionActivities([]);
        } finally {
          setLoadingActiveSessionActivities(false);
        }
        setClosedSessionsWithoutActivities([]);
      } else {
        // Если нет активной смены, проверяем закрытые смены с неуказанными активностями
        // Ищем все такие смены (без ограничения по дате)
        console.log('[MyShiftCardV2] Поиск закрытых смен с неуказанными активностями:', { siteId });
        
        // Используем API для получения закрытых смен с неуказанными активностями
        // Не передаем даты - получаем все такие смены
        const closedSessions = await sessionsV2Api.getMyClosedSessionsWithoutActivities(siteId);
        
        console.log('[MyShiftCardV2] Найдено закрытых смен с неуказанными активностями:', closedSessions.length);
        
        setClosedSessionsWithoutActivities(closedSessions);
        setActiveSessionActivities([]);
      }
    } catch (error) {
      console.error('Ошибка загрузки смен:', error);
      setActiveSession(null);
      setClosedSessionsWithoutActivities([]);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, siteId]);

  // Обновление времени работы и заработка
  const updateTimeAndEarnings = useCallback(() => {
    if (!activeSession) {
      setElapsedTime('');
      setEarnedAmount(0);
      return;
    }

    const startTime = new Date(activeSession.timeStart);
    const now = currentTime;
    const diffMs = now.getTime() - startTime.getTime();
    
    const hours = diffMs / (1000 * 60 * 60);
    const hoursFormatted = hours.toFixed(2);
    setElapsedTime(hoursFormatted);
    
    // Рассчитываем заработок: часы * ставка
    const rate = activeSession.rate || 0;
    const earned = hours * rate;
    setEarnedAmount(Math.round(earned * 100) / 100);
  }, [activeSession, currentTime]);

  // Обновление времени каждую секунду для таймера
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Обновление времени работы при изменении смены или времени
  useEffect(() => {
    updateTimeAndEarnings();
  }, [updateTimeAndEarnings]);

  // Загрузка данных при монтировании
  useEffect(() => {
    loadActiveSession();
  }, [loadActiveSession]);

  // Обработчики SignalR событий для обновления своей смены в реальном времени
  const handleSessionCreated = useCallback((session: SessionEvent) => {
    // Проверяем, что это наша смена для текущего объекта
    if (session.siteId === siteId && session.userId === user?.id && !session.timeEnd) {
      // Преобразуем SessionEvent в SessionV2 и обновляем состояние
      const newSession: SessionV2 = {
        id: session.id,
        userId: session.userId,
        siteId: session.siteId,
        typeOfWorks: session.typeOfWorks || 0,
        timeStart: session.timeStart,
        timeEnd: session.timeEnd,
        rate: session.rate,
        contractId: session.contractId,
        siteName: session.siteName,
        siteClosed: session.siteClosed || false,
        userName: session.userName,
        userClosed: session.userClosed || false,
        typeOfWorkName: session.typeOfWorkName,
        typeOfWorkClosed: session.typeOfWorkClosed || false,
        typeOfWorkSection: session.typeOfWorkSection,
        hasFinancialAccess: session.hasFinancialAccess || true,
      };
      setActiveSession(newSession);
    }
  }, [siteId, user?.id]);

  const handleSessionUpdated = useCallback(async (session: SessionEvent) => {
    // Проверяем, что это наша смена для текущего объекта
    if (session.siteId === siteId && session.userId === user?.id) {
      if (!session.timeEnd) {
        // Смена активна - обновляем
        const updatedSession: SessionV2 = {
          id: session.id,
          userId: session.userId,
          siteId: session.siteId,
          typeOfWorks: session.typeOfWorks || 0,
          timeStart: session.timeStart,
          timeEnd: session.timeEnd,
          rate: session.rate,
          contractId: session.contractId,
          siteName: session.siteName,
          siteClosed: session.siteClosed || false,
          userName: session.userName,
          userClosed: session.userClosed || false,
          typeOfWorkName: session.typeOfWorkName,
          typeOfWorkClosed: session.typeOfWorkClosed || false,
          typeOfWorkSection: session.typeOfWorkSection,
          hasFinancialAccess: session.hasFinancialAccess || true,
        };
        setActiveSession(updatedSession);
        
        // Перезагружаем активности для открытой сессии
        try {
          const activities = await sessionsV2Api.getSessionActivities(siteId, session.id);
          setActiveSessionActivities(activities);
        } catch (error) {
          console.error('Ошибка загрузки активностей после обновления:', error);
        }
      } else {
        // Смена закрыта - очищаем состояние
        setActiveSession(null);
        setActiveSessionActivities([]);
      }
    }
  }, [siteId, user?.id]);

  const handleSessionClosed = useCallback((session: SessionEvent) => {
    // Если закрыта наша смена - очищаем состояние активной смены и загружаем закрытую смену без активностей
    if (session.siteId === siteId && session.userId === user?.id) {
      setActiveSession(null);
      // Загружаем закрытые смены, чтобы показать смену без активностей
      loadActiveSession();
    }
  }, [siteId, user?.id, loadActiveSession]);

  const handleSessionDeleted = useCallback((session: SessionEvent) => {
    // Если удалена наша смена - очищаем состояние
    if (session.siteId === siteId && session.userId === user?.id) {
      setActiveSession(null);
    }
  }, [siteId, user?.id]);

  // Подписываемся на SignalR события для данного объекта
  // Используем уникальный subscriptionId для этого компонента
  useOptimizedSignalR({
    onSessionCreated: handleSessionCreated,
    onSessionUpdated: handleSessionUpdated,
    onSessionClosed: handleSessionClosed,
    onSessionDeleted: handleSessionDeleted
  }, {
    siteId,
    subscriptionId: `myshift_site_${siteId}`
  });


  // Парсит ISO-дату из API как UTC, если в строке нет суффикса Z или offset (иначе JS трактует как локальное время).
  const parseSessionTimeAsUtc = useCallback((isoString: string | undefined): Date => {
    if (!isoString) return new Date();
    if (typeof isoString !== 'string') return new Date(isoString);
    if (isoString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(isoString)) return new Date(isoString);
    return new Date(isoString + 'Z');
  }, []);

  // Вычисляет время окончания активности
  const getActivityEndTime = useCallback((items: ActivityItem[], index: number, sessionEndTime: Date): Date => {
    if (index < items.length - 1) {
      return items[index + 1].timeStart;
    }
    return sessionEndTime;
  }, []);

  // Пересчитывает статистику для всех активностей
  const recalculateStats = useCallback((items: ActivityItem[], currentStart: Date, currentEnd: Date): ActivityItem[] => {
    const sorted = [...items].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
    const totalSessionDuration = currentEnd.getTime() - currentStart.getTime();
    const MS_IN_HOUR = 3600000;
    
    return sorted.map((item, index) => {
      const endTime = index < sorted.length - 1 ? sorted[index + 1].timeStart : currentEnd;
      const duration = endTime.getTime() - item.timeStart.getTime();
      
      const hours = Math.max(0, duration / MS_IN_HOUR);
      const percentage = totalSessionDuration > 0 ? (duration / totalSessionDuration) * 100 : 0;
      const amount = hours * item.rate;
      
      return {
        ...item,
        hours,
        percentage,
        amount
      };
    });
  }, []);

  // Инициализация формы при открытии модального окна для заполнения активностей
  useEffect(() => {
    if (sessionForActivities && showActivitiesSheet) {
      // Устанавливаем время окончания смены
      if (sessionForActivities.timeEnd) {
        const endTime = parseSessionTimeAsUtc(sessionForActivities.timeEnd);
        // Форматируем локальное время для input[type="datetime-local"]
        const year = endTime.getFullYear();
        const month = String(endTime.getMonth() + 1).padStart(2, '0');
        const day = String(endTime.getDate()).padStart(2, '0');
        const hours = String(endTime.getHours()).padStart(2, '0');
        const minutes = String(endTime.getMinutes()).padStart(2, '0');
        setTimeEnd(`${year}-${month}-${day}T${hours}:${minutes}`);
      } else {
        const now = new Date();
        // Форматируем локальное время для input[type="datetime-local"]
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        setTimeEnd(`${year}-${month}-${day}T${hours}:${minutes}`);
      }
      
      // Загружаем существующие активности (если есть)
      const loadExistingActivities = async () => {
        try {
          const existingActivities = await sessionsV2Api.getSessionActivities(siteId, sessionForActivities.id);
          // Загружаем все активности, включая "Не указано" (TypeOfWorkId <= 1)
          
          if (existingActivities.length > 0) {
            const sorted = [...existingActivities].sort((a, b) => {
              const oA = a.sortOrder ?? 999999;
              const oB = b.sortOrder ?? 999999;
              if (oA !== oB) return oA - oB;
              const tA = a.timeStart ? parseSessionTimeAsUtc(a.timeStart).getTime() : 0;
              const tB = b.timeStart ? parseSessionTimeAsUtc(b.timeStart).getTime() : 0;
              return tA - tB;
            });
            const sessionStart = parseSessionTimeAsUtc(sessionForActivities.timeStart);
            let cumulativeMs = sessionStart.getTime();
            const items: ActivityItem[] = sorted.map((activity) => {
              const isHoursOnly = !activity.timeStart && (activity.allocatedHours != null || activity.hours != null);
              let start: Date;
              if (activity.timeStart) {
                start = parseSessionTimeAsUtc(activity.timeStart);
              } else {
                start = new Date(cumulativeMs);
                const h = activity.hours ?? activity.allocatedHours ?? 0;
                cumulativeMs += h * 60 * 60 * 1000;
              }
              return {
                id: activity.id,
                typeOfWorkId: activity.typeOfWorkId || 1,
                typeOfWorkName: activity.typeOfWorkName,
                timeStart: start,
                rate: activity.rate,
                hours: activity.hours ?? activity.allocatedHours ?? 0,
                percentage: 0,
                amount: 0,
                isNew: false,
                isModified: false,
                comment: activity.comment,
                additionalWorkReason: activity.additionalWorkReason,
                isAdditionalWork: activity.isAdditionalWork,
                isHoursOnly
              };
            });
            const sessionEnd = sessionForActivities.timeEnd ? parseSessionTimeAsUtc(sessionForActivities.timeEnd) : new Date();
            const withStats = recalculateStats(items, sessionStart, sessionEnd);
            setActivities(withStats);
          } else {
            setActivities([]);
          }
        } catch (error) {
          console.error('Ошибка загрузки активностей:', error);
          setActivities([]);
        }
      };
      loadExistingActivities();
    }
  }, [sessionForActivities, showActivitiesSheet, siteId, recalculateStats, parseSessionTimeAsUtc]);

  // Пересчитываем статистику активностей при изменении времени
  useEffect(() => {
    if (activities.length > 0 && sessionForActivities) {
      const sessionStartTime = parseSessionTimeAsUtc(sessionForActivities.timeStart);
      const sessionEndTime = timeEnd ? new Date(timeEnd) : (sessionForActivities.timeEnd ? parseSessionTimeAsUtc(sessionForActivities.timeEnd) : new Date());
      setActivities(prev => recalculateStats(prev, sessionStartTime, sessionEndTime));
    }
  }, [timeEnd, sessionForActivities, recalculateStats, parseSessionTimeAsUtc]);

  useEffect(() => {
    if (showWorkTypeSelector && activitySourceTab === 'ставка') {
      const loadTypesOfWork = async () => {
        setLoadingWorkTypes(true);
        try {
          const data = await typesOfWorkV2Api.getTypesOfWork();
          setTypesOfWork(data);
        } catch (error) {
          console.error('Ошибка загрузки типов работ:', error);
          showError('Ошибка загрузки видов работ');
        } finally {
          setLoadingWorkTypes(false);
        }
      };
      loadTypesOfWork();
    }
  }, [showWorkTypeSelector, activitySourceTab, showError]);

  useEffect(() => {
    if (showWorkTypeSelector && activitySourceTab === 'сделка' && siteId) {
      const loadContracts = async () => {
        setLoadingContracts(true);
        try {
          const data = await contractsV2Api.getSiteContracts(siteId, { onlyActive: true });
          setContracts(data);
        } catch (error) {
          console.error('Ошибка загрузки контрактов:', error);
          showError('Ошибка загрузки контрактов');
        } finally {
          setLoadingContracts(false);
        }
      };
      loadContracts();
    }
  }, [showWorkTypeSelector, activitySourceTab, siteId, showError]);

  // Равномерно распределяет активности
  const distributeActivitiesEvenly = (
    items: typeof activities, 
    availableStart: Date, 
    availableEnd: Date
  ) => {
    if (items.length === 0) return items;
    
    const MIN_DURATION = 60 * 1000;
    const totalAvailable = availableEnd.getTime() - availableStart.getTime();
    const durationPerActivity = Math.max(MIN_DURATION, totalAvailable / items.length);
    
    return items.map((item, index) => {
      const newStartTime = availableStart.getTime() + (durationPerActivity * index);
      return {
        ...item,
        timeStart: new Date(newStartTime),
        isModified: true
      };
    });
  };

  const handleAddActivity = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setEditingActivityId(null);
    setActivitySourceTab('ставка');
    setSelectedWorkTypeIds([]);
    setSelectedContractIds([]);
    setWorkTypeSearchQuery('');
    setContractSearchQuery('');
    setSelectedWorkTypeCategory(null);
    setShowWorkTypeSelector(true);
  };

  const handleOpenActivityTypeEditor = useCallback((item: ActivityItem) => {
    setEditingActivityId(item.id);
    if (item.contractId) {
      setActivitySourceTab('сделка');
      setSelectedContractIds([item.contractId]);
      setSelectedWorkTypeIds([]);
    } else {
      setActivitySourceTab('ставка');
      setSelectedWorkTypeIds(item.typeOfWorkId ? [item.typeOfWorkId] : []);
      setSelectedContractIds([]);
    }
    setWorkTypeSearchQuery('');
    setContractSearchQuery('');
    setSelectedWorkTypeCategory(null);
    setShowWorkTypeSelector(true);
  }, []);

  const toggleWorkTypeSelection = (typeId: number) => {
    setSelectedWorkTypeIds(prev => {
      if (editingActivityId !== null) return prev[0] === typeId ? [] : [typeId];
      if (prev.includes(typeId)) return prev.filter(id => id !== typeId);
      return [...prev, typeId];
    });
  };

  const toggleContractSelection = (contractId: number) => {
    setSelectedContractIds(prev => {
      if (editingActivityId !== null) return prev[0] === contractId ? [] : [contractId];
      if (prev.includes(contractId)) return prev.filter(id => id !== contractId);
      return [...prev, contractId];
    });
  };

  const handleConfirmWorkTypeSelection = () => {
    const isDealMode = activitySourceTab === 'сделка';
    const itemsCount = isDealMode ? selectedContractIds.length : selectedWorkTypeIds.length;
    if (itemsCount === 0) {
      showError(isDealMode ? 'Выберите хотя бы один контракт' : 'Выберите хотя бы один вид работы');
      return;
    }
    if (!sessionForActivities) {
      showError('Смена не найдена');
      return;
    }

    if (editingActivityId !== null) {
      const isEditDeal = isDealMode;
      const contractId = isEditDeal ? selectedContractIds[0] : undefined;
      const typeId = isEditDeal ? undefined : selectedWorkTypeIds[0];
      if (isEditDeal && contractId) {
        const contract = contracts.find(c => c.id === contractId);
        if (contract) {
          updateActivity(editingActivityId, { typeOfWorkId: contract.typeOfWorkId, contractId: contract.id, contractTitle: contract.title });
        }
      } else if (!isEditDeal && typeId) {
        updateActivity(editingActivityId, { typeOfWorkId: typeId, contractId: undefined, contractTitle: undefined });
      }
      setShowWorkTypeSelector(false);
      setEditingActivityId(null);
      setSelectedWorkTypeIds([]);
      setSelectedContractIds([]);
      setWorkTypeSearchQuery('');
      setContractSearchQuery('');
      setSelectedWorkTypeCategory(null);
      showSuccess('Тип активности изменён');
      return;
    }

    const currentStart = parseSessionTimeAsUtc(sessionForActivities.timeStart);
    const currentEnd = timeEnd ? new Date(timeEnd) : (sessionForActivities.timeEnd ? parseSessionTimeAsUtc(sessionForActivities.timeEnd) : new Date());
    const sorted = [...activities].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
    const newActivities: typeof activities = [];
    const timestamp = Date.now();
    let nextActivityStartTime: number;
    if (sorted.length === 0) {
      nextActivityStartTime = currentStart.getTime();
    } else {
      const lastActivity = sorted[sorted.length - 1];
      const lastActivityEnd = getActivityEndTime(sorted, sorted.length - 1, currentEnd);
      nextActivityStartTime = lastActivityEnd.getTime() + 1000;
    }
    const availableTime = currentEnd.getTime() - nextActivityStartTime;
    const MIN_DURATION = 60 * 1000;
    const allHoursOnly = sorted.every(a => a.isHoursOnly);

    if (isDealMode) {
      const selectedContracts = contracts.filter(c => selectedContractIds.includes(c.id));
      const n = selectedContracts.length;
      if (availableTime < MIN_DURATION * n) {
        selectedContracts.forEach((contract, index) => {
          newActivities.push({
            id: `new_${timestamp}_${index}`,
            typeOfWorkId: contract.typeOfWorkId,
            contractId: contract.id,
            contractTitle: contract.title,
            timeStart: new Date(nextActivityStartTime),
            rate: 0,
            hours: 0,
            percentage: 0,
            amount: 0,
            isNew: true,
            isModified: false,
            isHoursOnly: allHoursOnly
          });
        });
        const allActivities = [...sorted, ...newActivities];
        setActivities(recalculateStats(distributeActivitiesEvenly(allActivities, currentStart, currentEnd), currentStart, currentEnd));
      } else {
        const durationPerActivity = Math.max(MIN_DURATION, availableTime / n);
        selectedContracts.forEach((contract, index) => {
          const activityStartTime = nextActivityStartTime + (durationPerActivity * index);
          newActivities.push({
            id: `new_${timestamp}_${index}`,
            typeOfWorkId: contract.typeOfWorkId,
            contractId: contract.id,
            contractTitle: contract.title,
            timeStart: new Date(activityStartTime),
            rate: 0,
            hours: 0,
            percentage: 0,
            amount: 0,
            isNew: true,
            isModified: false,
            isHoursOnly: allHoursOnly
          });
        });
        setActivities(recalculateStats([...sorted, ...newActivities], currentStart, currentEnd));
      }
    } else if (availableTime < MIN_DURATION * selectedWorkTypeIds.length) {
      selectedWorkTypeIds.forEach((typeId, index) => {
        newActivities.push({
          id: `new_${timestamp}_${index}`,
          typeOfWorkId: typeId,
          timeStart: new Date(nextActivityStartTime),
          rate: sessionForActivities?.rate || 0,
          hours: 0,
          percentage: 0,
          amount: 0,
          isNew: true,
          isModified: false,
          isHoursOnly: allHoursOnly
        });
      });
      
      const allActivities = [...sorted, ...newActivities];
      const redistributed = distributeActivitiesEvenly(allActivities, currentStart, currentEnd);
      const withStats = recalculateStats(redistributed, currentStart, currentEnd);
      
      setActivities(withStats);
    } else {
      // Распределяем новые активности в доступном пространстве
      const durationPerActivity = Math.max(MIN_DURATION, availableTime / selectedWorkTypeIds.length);
      
      selectedWorkTypeIds.forEach((typeId, index) => {
        const activityStartTime = nextActivityStartTime + (durationPerActivity * index);
        newActivities.push({
          id: `new_${timestamp}_${index}`,
          typeOfWorkId: typeId,
          timeStart: new Date(activityStartTime),
          rate: sessionForActivities?.rate || 0,
          hours: 0,
          percentage: 0,
          amount: 0,
          isNew: true,
          isModified: false,
          isHoursOnly: allHoursOnly
        });
      });
      
      const allActivities = [...sorted, ...newActivities];
      const withStats = recalculateStats(allActivities, currentStart, currentEnd);
      
      setActivities(withStats);
    }
    
    setShowWorkTypeSelector(false);
    setSelectedWorkTypeIds([]);
    setSelectedContractIds([]);
    setWorkTypeSearchQuery('');
    setContractSearchQuery('');
    setSelectedWorkTypeCategory(null);
    
    showSuccess(`Добавлено активностей: ${newActivities.length}`);
  };

  // Закрывает модальное окно выбора видов работ
  const handleCloseWorkTypeSelector = () => {
    setShowWorkTypeSelector(false);
    setEditingActivityId(null);
    setSelectedWorkTypeIds([]);
    setSelectedContractIds([]);
    setWorkTypeSearchQuery('');
    setContractSearchQuery('');
    setSelectedWorkTypeCategory(null);
  };

  // Удаление активности
  const handleDeleteActivity = (id: number | string) => {
    if (!sessionForActivities) return;
    const currentStart = parseSessionTimeAsUtc(sessionForActivities.timeStart);
    const currentEnd = timeEnd ? new Date(timeEnd) : (sessionForActivities.timeEnd ? parseSessionTimeAsUtc(sessionForActivities.timeEnd) : new Date());
    const updated = activities.filter(a => a.id !== id);
    const final = recalculateStats(updated, currentStart, currentEnd);
    setActivities(final);
    showSuccess('Активность удалена');
  };

  // Обновляет активность (можно редактировать вид работы и время активности, ставка недоступна)
  const updateActivity = useCallback((id: number | string, updates: Partial<ActivityItem>) => {
    if (!sessionForActivities) return;
    const currentStart = parseSessionTimeAsUtc(sessionForActivities.timeStart);
    const currentEnd = timeEnd ? new Date(timeEnd) : (sessionForActivities.timeEnd ? parseSessionTimeAsUtc(sessionForActivities.timeEnd) : new Date());
    
    setActivities(prev => {
      const sorted = [...prev].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
      const currentIndex = sorted.findIndex(item => item.id === id);
      
      if (currentIndex === -1) {
        return prev;
      }
      
      const updated = [...sorted];
      
      if (updates.timeStart) {
        const newStart = updates.timeStart.getTime();
        
        const minStart = currentIndex > 0 
          ? sorted[currentIndex - 1].timeStart.getTime() + 60000
          : currentStart.getTime();
        
        const maxStart = currentIndex < sorted.length - 1
          ? sorted[currentIndex + 1].timeStart.getTime() - 60000
          : currentEnd.getTime() - 60000;
        
        const clampedStart = Math.max(minStart, Math.min(newStart, maxStart));
        
        updated[currentIndex] = {
          ...updated[currentIndex],
          timeStart: new Date(clampedStart),
          isModified: true
        };
      }
      
      // Вид работы можно редактировать, ставка недоступна
      if (updates.typeOfWorkId !== undefined) {
        updated[currentIndex] = {
          ...updated[currentIndex],
          typeOfWorkId: updates.typeOfWorkId,
          isModified: true
        };
      }

      // Комментарий и пометка «доп.работа» (иначе иконки не обновляются)
      if (updates.comment !== undefined || updates.isAdditionalWork !== undefined || updates.additionalWorkReason !== undefined) {
        updated[currentIndex] = {
          ...updated[currentIndex],
          ...(updates.comment !== undefined && { comment: updates.comment }),
          ...(updates.isAdditionalWork !== undefined && { isAdditionalWork: updates.isAdditionalWork }),
          ...(updates.additionalWorkReason !== undefined && { additionalWorkReason: updates.additionalWorkReason }),
          isModified: true
        };
      }
      
      return recalculateStats(updated, currentStart, currentEnd);
    });
  }, [sessionForActivities, timeEnd, recalculateStats, parseSessionTimeAsUtc]);

  // Форматирование времени
  const formatTime = useCallback((date: Date) => {
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  }, []);

  const formatDuration = useCallback((hours: number) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return { hours: h, minutes: m };
  }, []);

  // Обработчик начала смены - показывает окно подтверждения
  const handleStartShift = () => {
    setShowConfirmStartShift(true);
  };

  // Фактическое начало смены после подтверждения
  const performStartShift = async () => {
    if (!user?.id) return;
    
    setIsStarting(true);
    setShowConfirmStartShift(false);
    try {
      const data: StartMySessionV2Request = {};
      
      await sessionsV2Api.startMySession(siteId, data);
      await loadActiveSession();
    } catch (error: any) {
      console.error('Ошибка при начале смены:', error);
      
      // Извлекаем сообщение об ошибке из ответа сервера
      let errorMessage = 'Ошибка при начале смены. Попробуйте еще раз.';
      
      if (error?.response) {
        // Сервер возвращает ошибку как строку в StatusCode(400, ex.Message)
        // или как объект { error: string } или { message: string }
        const responseData = error.response.data;
        
        if (typeof responseData === 'string') {
          // Если это строка (StatusCode(400, ex.Message))
          errorMessage = responseData;
        } else if (responseData?.error) {
          // Если это объект с полем error
          errorMessage = responseData.error;
        } else if (responseData?.message) {
          // Если это объект с полем message
          errorMessage = responseData.message;
        } else if (responseData) {
          // Если это другой объект, попробуем преобразовать в строку
          errorMessage = String(responseData);
        }
      } else if (error?.message) {
        // Если ошибка без response, используем message
        errorMessage = error.message;
      }
      
      showError(errorMessage);
    } finally {
      setIsStarting(false);
    }
  };

  // Обработчик завершения смены - показывает окно подтверждения
  const handleEndShift = () => {
    setShowConfirmEndShift(true);
  };

  // Фактическое завершение смены после подтверждения
  const performEndShift = async () => {
    if (!activeSession?.id) return;
    
    setIsEnding(true);
    setShowConfirmEndShift(false);
    try {
      // Закрываем смену без активностей (typeOfWorks не передаем)
      const data: EndMySessionV2Request = {
        sessionId: activeSession.id
      };
      
      await sessionsV2Api.endMySession(siteId, data);
      showSuccess('Смена завершена');
      await loadActiveSession();
    } catch (error: any) {
      console.error('Ошибка при завершении смены:', error);
      const data = error?.response?.data;
      const msg = typeof data === 'string' ? data : (data?.error ?? data?.message) ?? error?.message ?? 'Ошибка при завершении смены. Попробуйте еще раз.';
      showError(typeof msg === 'string' ? msg : 'Ошибка при завершении смены. Попробуйте еще раз.');
    } finally {
      setIsEnding(false);
    }
  };

  // Обработчик добавления контрольной точки - показывает окно подтверждения
  const handleAddCheckpoint = () => {
    setShowConfirmAddCheckpoint(true);
  };

  // Фактическое добавление контрольной точки после подтверждения
  const performAddCheckpoint = async () => {
    if (!activeSession?.id) return;
    
    setIsAddingCheckpoint(true);
    setShowConfirmAddCheckpoint(false);
    try {
      await sessionsV2Api.addCheckpointToMySession(siteId, activeSession.id);
      showSuccess('Контрольная точка добавлена');
      await loadActiveSession();
    } catch (error: any) {
      console.error('Ошибка при добавлении контрольной точки:', error);
      
      let errorMessage = 'Ошибка при добавлении контрольной точки. Попробуйте еще раз.';
      
      if (error?.response) {
        const responseData = error.response.data;
        
        if (typeof responseData === 'string') {
          errorMessage = responseData;
        } else if (responseData?.error) {
          errorMessage = responseData.error;
        } else if (responseData?.message) {
          errorMessage = responseData.message;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      showError(errorMessage);
    } finally {
      setIsAddingCheckpoint(false);
    }
  };

  // Обработчик добавления активности с длительностью
  const handleAddActivityWithDuration = async () => {
    if (!activeSession?.id) return;
    
    // Проверяем, что указана длительность
    if (activityHours === 0 && activityMinutes === 0) {
      showError('Укажите длительность активности');
      return;
    }
    
    const now = new Date();
    const durationMs = (activityHours * 60 + activityMinutes) * 60 * 1000;
    const activityStartTime = new Date(now.getTime() - durationMs);
    const sessionStartTime = new Date(activeSession.timeStart);
    
    // Проверяем, что время начала активности не раньше времени начала смены
    if (activityStartTime.getTime() < sessionStartTime.getTime()) {
      showError('Время начала активности не может быть раньше времени начала смены');
      return;
    }
    
    // Получаем текущие активности для проверки пересечений
    const existingActivities = await sessionsV2Api.getSessionActivities(siteId, activeSession.id);
    
    // Находим активности, которые пересекаются с новой активностью (только time-based; hours-only пропускаем)
    const overlappingActivities = existingActivities.filter(activity => {
      if (!activity.timeStart) return false;
      const activityStart = new Date(activity.timeStart);
      const activityEnd = activity.timeEnd ? new Date(activity.timeEnd) : now;
      
      // Проверяем пересечение: новая активность начинается раньше существующей
      return activityStart.getTime() >= activityStartTime.getTime() && 
             activityStart.getTime() < now.getTime();
    });
    
    // Если есть пересекающиеся активности, показываем подтверждение
    if (overlappingActivities.length > 0) {
      setActivitiesToDelete(overlappingActivities);
      setPendingActivityData({ startTime: activityStartTime, endTime: now });
      setShowAddActivitySheet(false);
      setShowConfirmDeleteSheet(true);
      return;
    }
    
    // Если пересечений нет, продолжаем добавление
    await performAddActivity(activityStartTime, now);
  };

  // Выполнение добавления активности (после подтверждения)
  const performAddActivity = async (activityStartTime: Date, now: Date) => {
    if (!activeSession?.id) return;
    
    setIsAddingActivity(true);
    try {
      const sessionStartTime = new Date(activeSession.timeStart);
      
      // Получаем текущие активности
      const existingActivities = await sessionsV2Api.getSessionActivities(siteId, activeSession.id);
      const activeActivities = existingActivities.filter(a => !a.timeEnd);
      
      // Закрываем все активные активности (если есть)
      for (const currentActivity of activeActivities) {
        const updateData: UpdateSessionActivityRequest = {
          typeOfWorkId: currentActivity.typeOfWorkId,
          timeStart: currentActivity.timeStart,
          timeEnd: activityStartTime.toISOString(),
          rate: currentActivity.rate || 0,
          contractId: currentActivity.contractId,
          comment: currentActivity.comment || ''
        };
        await sessionsV2Api.updateSessionActivity(siteId, activeSession.id, currentActivity.id, updateData);
      }
      
      // Удаляем пересекающиеся активности (если были)
      for (const activityToDelete of activitiesToDelete) {
        try {
          await sessionsV2Api.deleteSessionActivity(siteId, activeSession.id, activityToDelete.id);
        } catch (error) {
          console.warn(`Failed to delete activity ${activityToDelete.id}:`, error);
        }
      }
      
      // Создаем новую активность в прошлом (закрытую)
      const pastActivityData: CreateSessionActivityRequest = {
        typeOfWorkId: 1, // "Не указано" по умолчанию
        timeStart: activityStartTime.toISOString(),
        timeEnd: now.toISOString(),
        rate: 0,
        contractId: undefined,
        comment: ''
      };
      await sessionsV2Api.createSessionActivity(siteId, activeSession.id, pastActivityData);
      
      // Создаем новую активную активность (начинается сейчас)
      const newActivityData: CreateSessionActivityRequest = {
        typeOfWorkId: 1, // "Не указано" по умолчанию
        timeStart: now.toISOString(),
        timeEnd: undefined, // Открытая активность
        rate: 0,
        contractId: undefined,
        comment: ''
      };
      await sessionsV2Api.createSessionActivity(siteId, activeSession.id, newActivityData);
      
      showSuccess('Активность добавлена');
      setShowAddActivitySheet(false);
      setShowConfirmDeleteSheet(false);
      setActivityHours(1);
      setActivityMinutes(0);
      setActivitiesToDelete([]);
      setPendingActivityData(null);
      await loadActiveSession();
    } catch (error: any) {
      console.error('Ошибка при добавлении активности:', error);
      
      let errorMessage = 'Ошибка при добавлении активности. Попробуйте еще раз.';
      
      if (error?.response) {
        const responseData = error.response.data;
        
        if (typeof responseData === 'string') {
          errorMessage = responseData;
        } else if (responseData?.error) {
          errorMessage = responseData.error;
        } else if (responseData?.message) {
          errorMessage = responseData.message;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      showError(errorMessage);
    } finally {
      setIsAddingActivity(false);
    }
  };

  // Обработчик подтверждения удаления пересекающихся активностей
  const handleConfirmDelete = async () => {
    if (!pendingActivityData) return;
    await performAddActivity(pendingActivityData.startTime, pendingActivityData.endTime);
  };

  // Обработчик открытия модального окна для заполнения активностей закрытой смены
  const handleOpenActivitiesSheet = (session: SessionV2) => {
    setSessionForActivities(session);
    setShowActivitiesSheet(true);
  };

  // Обработчик сохранения активностей для закрытой смены
  const handleSaveActivities = async () => {
    if (!sessionForActivities?.id) {
      showError('Смена не найдена');
      return;
    }

    if (activities.length === 0) {
      showError('Добавьте хотя бы одну активность');
      return;
    }

    // Проверяем, что все активности имеют typeOfWorkId (включая "Не указано" = 1)
    const incompleteActivities = activities.filter(a => !a.typeOfWorkId || a.typeOfWorkId === 0);
    if (incompleteActivities.length > 0) {
      showError('Выберите вид работы для всех активностей');
      return;
    }

    // Проверяем, что все активности имеют корректное время (время смены парсим как UTC, как отдаёт API)
    const sessionStart = parseSessionTimeAsUtc(sessionForActivities.timeStart);
    const sessionEnd = timeEnd ? new Date(timeEnd) : (sessionForActivities.timeEnd ? parseSessionTimeAsUtc(sessionForActivities.timeEnd) : new Date());
    const sortedForValidation = [...activities].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
    
    for (let i = 0; i < sortedForValidation.length; i++) {
      const activity = sortedForValidation[i];
      if (activity.isHoursOnly) continue;
      if (activity.timeStart.getTime() < sessionStart.getTime()) {
        showError('Время начала активности не может быть раньше начала смены');
        return;
      }
      const activityEnd = getActivityEndTime(sortedForValidation, i, sessionEnd);
      if (activityEnd.getTime() > sessionEnd.getTime()) {
        showError('Время окончания активности не может быть позже окончания смены');
        return;
      }
    }

    setIsEnding(true);
    try {
      // Сортируем активности по времени начала
      const sortedActivities = [...activities].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
      
      // Преобразуем активности в формат для API
      const activitiesData: SessionActivityUpdateRequest[] = sortedActivities.map((item, index) => {
        if (item.isHoursOnly) {
          return {
            id: typeof item.id === 'number' && item.id > 0 ? item.id : undefined,
            typeOfWorkId: item.typeOfWorkId,
            timeStart: undefined,
            timeEnd: undefined,
            allocatedHours: item.hours,
            sortOrder: index,
            rate: item.rate || 0,
            contractId: item.contractId,
            comment: item.comment ?? '',
            isAdditionalWork: item.isAdditionalWork,
            additionalWorkReason: item.additionalWorkReason,
          };
        }
        const isFirstActivity = index === 0;
        const isLastActivity = index === sortedActivities.length - 1;
        const isSessionClosed = !!sessionForActivities.timeEnd;
        
        const activityStartTime = isFirstActivity ? sessionStart : item.timeStart;
        const endTime = isLastActivity ? sessionEnd : getActivityEndTime(sortedActivities, index, sessionEnd);
        const activityId = typeof item.id === 'number' && item.id > 0 ? item.id : undefined;
        const activityEndTime = (isLastActivity && !isSessionClosed) ? undefined : endTime.toISOString();
        
        return {
          id: activityId,
          typeOfWorkId: item.typeOfWorkId,
          timeStart: activityStartTime.toISOString(),
          timeEnd: activityEndTime,
          rate: item.rate || 0,
          contractId: item.contractId,
          comment: item.comment ?? '',
          isAdditionalWork: item.isAdditionalWork,
          additionalWorkReason: item.additionalWorkReason,
        };
      });

      // Используем updateSession для синхронизации активностей
      // Это автоматически обновит существующие, создаст новые и удалит отсутствующие
      const sessionData: UpdateSessionV2Request = {
        timeStart: sessionForActivities.timeStart,
        closeSession: !!sessionForActivities.timeEnd,
        ...(sessionForActivities.timeEnd ? { timeEnd: sessionForActivities.timeEnd } : {}),
        activities: activitiesData
      };

      await sessionsV2Api.updateMySession(siteId, sessionForActivities.id, sessionData);
      
      showSuccess('Активности успешно сохранены');
      
      setShowActivitiesSheet(false);
      setSessionForActivities(null);
      setActivities([]);
      await loadActiveSession();
    } catch (error: any) {
      console.error('Ошибка при сохранении активностей:', error);
      const data = error?.response?.data;
      const errorMessage = typeof data === 'string' ? data : (data?.error ?? data?.message) ?? error?.message ?? 'Ошибка при сохранении активностей. Попробуйте еще раз.';
      showError(typeof errorMessage === 'string' ? errorMessage : 'Ошибка при сохранении активностей. Попробуйте еще раз.');
    } finally {
      setIsEnding(false);
    }
  };

  // Обработчик закрытия bottom sheet для заполнения активностей
  const handleCloseActivitiesSheet = () => {
    setShowActivitiesSheet(false);
    setSessionForActivities(null);
    setActivities([]);
  };

  const isImageFile = (file: File): boolean =>
    (file.type && file.type.startsWith('image/')) ||
    ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.heic', '.heif'].some((ext) =>
      file.name.toLowerCase().endsWith(ext)
    );

  // Открыть окно комментария к активности (в окне «Указать виды работ»)
  const handleCommentClick = (item: ActivityItem) => {
    setCurrentActivityForComment(item);
    setActivityComment(item.comment ?? '');
    setCommentPhotos([]);
    setCommentPhotoPreviews([]);
    setShowCommentSheet(true);
  };

  useEffect(() => {
    if (!showCommentSheet || commentPhotos.length === 0) {
      commentPhotoUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      commentPhotoUrlsRef.current.clear();
      setCommentPhotoPreviews([]);
      return;
    }
    const urls = commentPhotos.map((f) => URL.createObjectURL(f));
    urls.forEach((u) => commentPhotoUrlsRef.current.add(u));
    setCommentPhotoPreviews(urls);
    return () => {
      urls.forEach((u) => {
        URL.revokeObjectURL(u);
        commentPhotoUrlsRef.current.delete(u);
      });
    };
  }, [showCommentSheet, commentPhotos]);

  const handleCommentPhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const valid = files.filter((f) => f.size > 0 && isImageFile(f));
    const max = 10;
    const toAdd = valid.slice(0, max - commentPhotos.length);
    setCommentPhotos((prev) => [...prev, ...toAdd]);
    if (commentFileInputRef.current) commentFileInputRef.current.value = '';
  };

  const handleRemoveCommentPhoto = (index: number) => {
    setCommentPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCloseCommentSheet = () => {
    setShowCommentSheet(false);
    setCurrentActivityForComment(null);
    setActivityComment('');
    setCommentPhotos([]);
    setCommentPhotoPreviews([]);
  };

  const handleSaveComment = async () => {
    if (!currentActivityForComment) {
      handleCloseCommentSheet();
      return;
    }
    updateActivity(currentActivityForComment.id, { comment: activityComment ?? '' });
    const activityId = currentActivityForComment.id;
    const canUploadPhotos = typeof activityId === 'number' && sessionForActivities && commentPhotos.length > 0;
    if (canUploadPhotos) {
      try {
        const opts = { maxWidth: 1600, maxHeight: 1600, maxSizeMB: 0.5, outputFormat: 'image/jpeg' as const };
        const compressed = await compressImageFiles(commentPhotos, opts);
        await sessionsV2Api.uploadActivityCommentPhotos(siteId, sessionForActivities.id, activityId, compressed);
      } catch (err) {
        console.error('Ошибка загрузки фото комментария:', err);
        showError('Комментарий сохранён, но не удалось загрузить фото');
      }
    }
    if (commentPhotos.length > 0 && typeof activityId !== 'number') {
      showSuccess('Комментарий сохранён. Сохраните активности, затем можно добавить фото к этой активности.');
    } else {
      showSuccess(canUploadPhotos && commentPhotos.length > 0 ? 'Комментарий и фото сохранены' : 'Комментарий сохранён');
    }
    handleCloseCommentSheet();
  };

  // Открыть окно доп.работы (в окне «Указать виды работ»)
  const handleAdditionalWorkClick = (item: ActivityItem) => {
    setCurrentActivityForAdditionalWork(item);
    setAdditionalWorkReason(item.additionalWorkReason ?? '');
    setAdditionalWorkPhotos([]);
    setAdditionalWorkPhotoPreviews([]);
    setShowAdditionalWorkSheet(true);
  };

  useEffect(() => {
    if (!showAdditionalWorkSheet || additionalWorkPhotos.length === 0) {
      additionalWorkPhotoUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      additionalWorkPhotoUrlsRef.current.clear();
      setAdditionalWorkPhotoPreviews([]);
      return;
    }
    const urls = additionalWorkPhotos.map((f) => URL.createObjectURL(f));
    urls.forEach((u) => additionalWorkPhotoUrlsRef.current.add(u));
    setAdditionalWorkPhotoPreviews(urls);
    return () => {
      urls.forEach((u) => {
        URL.revokeObjectURL(u);
        additionalWorkPhotoUrlsRef.current.delete(u);
      });
    };
  }, [showAdditionalWorkSheet, additionalWorkPhotos]);

  const handleAdditionalWorkPhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const valid = files.filter((f) => f.size > 0 && isImageFile(f));
    const max = 10;
    const toAdd = valid.slice(0, max - additionalWorkPhotos.length);
    setAdditionalWorkPhotos((prev) => [...prev, ...toAdd]);
    if (additionalWorkFileInputRef.current) additionalWorkFileInputRef.current.value = '';
  };

  const handleRemoveAdditionalWorkPhoto = (index: number) => {
    setAdditionalWorkPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadAdditionalWorkPhotosIfNeeded = async () => {
    if (!currentActivityForAdditionalWork || !sessionForActivities || additionalWorkPhotos.length === 0) return;
    const activityId = currentActivityForAdditionalWork.id;
    if (typeof activityId !== 'number') return;
    try {
      // Сначала создаём флаг «доп.работа» на сервере (если ещё нет), иначе загрузка фото вернёт 404
      if (currentActivityForAdditionalWork.isAdditionalWork) {
        try {
          await sessionsV2Api.markActivityAdditionalWork(siteId, sessionForActivities.id, activityId, {
            reason: currentActivityForAdditionalWork.additionalWorkReason ?? additionalWorkReason ?? '',
          });
        } catch (markErr: unknown) {
          const res = (markErr as { response?: { data?: unknown; status?: number } })?.response;
          const data = res?.data;
          const msg = typeof data === 'string' ? data : (data && typeof data === 'object' && 'error' in data ? (data as { error?: string }).error : undefined);
          const alreadyMarked = res?.status === 400 && (msg === 'Activity already marked as additional work' || (typeof msg === 'string' && msg.includes('already marked')));
          if (!alreadyMarked) throw markErr;
        }
      }
      const opts = { maxWidth: 1600, maxHeight: 1600, maxSizeMB: 0.5, outputFormat: 'image/jpeg' as const };
      const compressed = await compressImageFiles(additionalWorkPhotos, opts);
      await sessionsV2Api.uploadAdditionalWorkPhotos(siteId, sessionForActivities.id, activityId, compressed);
    } catch (err) {
      console.error('Ошибка загрузки фото доп.работы:', err);
      showError('Пометка сохранена, но не удалось загрузить фото');
    }
  };

  const handleCloseAdditionalWorkSheet = async () => {
    if (currentActivityForAdditionalWork?.isAdditionalWork && additionalWorkReason !== (currentActivityForAdditionalWork.additionalWorkReason ?? '')) {
      updateActivity(currentActivityForAdditionalWork.id, { additionalWorkReason: additionalWorkReason ?? '' });
    }
    await uploadAdditionalWorkPhotosIfNeeded();
    setShowAdditionalWorkSheet(false);
    setCurrentActivityForAdditionalWork(null);
    setAdditionalWorkReason('');
    setAdditionalWorkPhotos([]);
    setAdditionalWorkPhotoPreviews([]);
  };

  const handleToggleAdditionalWork = async () => {
    if (!currentActivityForAdditionalWork) return;
    const isCurrentlyDop = currentActivityForAdditionalWork.isAdditionalWork;
    updateActivity(currentActivityForAdditionalWork.id, {
      isAdditionalWork: !isCurrentlyDop,
      additionalWorkReason: isCurrentlyDop ? '' : (additionalWorkReason ?? ''),
    });
    await uploadAdditionalWorkPhotosIfNeeded();
    showSuccess(isCurrentlyDop ? 'Пометка «доп.работа» снята' : 'Активность помечена как доп.работа');
    setShowAdditionalWorkSheet(false);
    setCurrentActivityForAdditionalWork(null);
    setAdditionalWorkReason('');
    setAdditionalWorkPhotos([]);
    setAdditionalWorkPhotoPreviews([]);
  };

  const handleRemoveAdditionalWorkMark = async () => {
    if (currentActivityForAdditionalWork) {
      updateActivity(currentActivityForAdditionalWork.id, { isAdditionalWork: false, additionalWorkReason: '' });
      showSuccess('Пометка «доп.работа» снята');
    }
    setShowAdditionalWorkSheet(false);
    setCurrentActivityForAdditionalWork(null);
    setAdditionalWorkReason('');
    setAdditionalWorkPhotos([]);
    setAdditionalWorkPhotoPreviews([]);
  };

  // Форматирование времени начала
  const formatStartTime = (timeString: string) => {
    return new Date(timeString).toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Форматирование времени работы
  const formatElapsedTime = (hours: string): string => {
    const hoursNum = parseFloat(hours);
    const h = Math.floor(hoursNum);
    const m = Math.floor((hoursNum - h) * 60);
    
    if (h > 0) {
      return `${h}ч ${m}м`;
    } else {
      return `${m}м`;
    }
  };

  // Функция для склонения слова "смена"
  const getSessionWordForm = (count: number): string => {
    if (count === 1) {
      return 'смене';
    } else if (count >= 2 && count <= 4) {
      return 'сменах';
    } else {
      return 'сменах';
    }
  };

  // Фильтруем типы работ для модального окна
  const filteredTypes = typesOfWork.filter(type => {
    const matchesSearch = type.name.toLowerCase().includes(workTypeSearchQuery.toLowerCase()) ||
                         type.section.toLowerCase().includes(workTypeSearchQuery.toLowerCase());
    const matchesCategory = !selectedWorkTypeCategory || type.section === selectedWorkTypeCategory;
    return matchesSearch && matchesCategory;
  });

  const groupedTypes = filteredTypes.reduce((acc, type) => {
    if (!acc[type.section]) {
      acc[type.section] = [];
    }
    acc[type.section].push(type);
    return acc;
  }, {} as Record<string, TypeOfWorkV2[]>);

  const sortedActivities = useMemo(() => {
    return [...activities].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
  }, [activities]);

  // Получаем времена пересечений (между активностями)
  const intersectionTimes = useMemo((): Date[] => {
    if (sortedActivities.length < 2 || !sessionForActivities) return [];
    return sortedActivities.slice(1).map((activity, arrayIndex) => {
      const handleIndex = arrayIndex;
      if (tempTimelinePositions && tempTimelinePositions.has(handleIndex)) {
        return tempTimelinePositions.get(handleIndex)!;
      }
      return activity.timeStart;
    });
  }, [sortedActivities, tempTimelinePositions, sessionForActivities]);

  // Получаем информацию о сегментах временной шкалы
  const timelineSegments = useMemo((): Array<{ start: Date; end: Date; activity: ActivityItem; duration?: number }> => {
    if (sortedActivities.length === 0 || !sessionForActivities) return [];
    
    const currentStart = parseSessionTimeAsUtc(sessionForActivities.timeStart);
    const currentEnd = timeEnd ? new Date(timeEnd) : (sessionForActivities.timeEnd ? parseSessionTimeAsUtc(sessionForActivities.timeEnd) : new Date());
    
    return sortedActivities.map((activity, index) => {
      let start: Date;
      let end: Date;
      
      // Начало сегмента
      if (index === 0) {
        start = currentStart;
      } else {
        const handleIndex = index - 1;
        if (tempTimelinePositions && tempTimelinePositions.has(handleIndex)) {
          start = tempTimelinePositions.get(handleIndex)!;
        } else {
          start = activity.timeStart;
        }
      }
      
      // Конец сегмента
      if (index < sortedActivities.length - 1) {
        const handleIndex = index;
        if (tempTimelinePositions && tempTimelinePositions.has(handleIndex)) {
          end = tempTimelinePositions.get(handleIndex)!;
        } else {
          end = sortedActivities[index + 1].timeStart;
        }
      } else {
        end = currentEnd;
      }
      
      const duration = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
      return { start, end, activity, duration };
    });
  }, [sortedActivities, sessionForActivities, timeEnd, tempTimelinePositions, parseSessionTimeAsUtc]);

  const hasUnsetActiveActivities =
    !loadingActiveSessionActivities &&
    activeSessionActivities.some((a) => !a.typeOfWorkId || a.typeOfWorkId <= 1);

  const renderCollapsedPreview = () => (
    <div className={styles.collapsedPreview}>
      {(isLoading || activeSession) && (
        <div className={styles.collapsedStatusRow}>
          {isLoading ? (
            <>
              <span className={styles.collapsedLabel}>Загрузка смены</span>
              <span className={styles.collapsedSkeleton} aria-hidden />
            </>
          ) : activeSession ? (
            <>
              <span className={styles.collapsedLabel}>Смена открыта</span>
              <span className={styles.collapsedHero}>
                {elapsedTime ? formatElapsedTime(elapsedTime) : '—'}
              </span>
              <span className={styles.collapsedMeta}>
                с {formatStartTime(activeSession.timeStart)}
                {activeSession.rate && earnedAmount > 0
                  ? ` · ${earnedAmount.toLocaleString('ru-RU')} ₽`
                  : ''}
              </span>
            </>
          ) : null}
        </div>
      )}

      <div className={styles.collapsedActions}>
        <StatusActionButton
          variant={isLoading ? 'secondary' : activeSession ? 'danger' : 'success'}
          fullWidth
          icon={isLoading ? undefined : activeSession ? 'ri-stop-circle-line' : 'ri-play-circle-line'}
          onClick={isLoading ? undefined : activeSession ? handleEndShift : handleStartShift}
          loading={isLoading || (activeSession ? isEnding : isStarting)}
          disabled={isLoading || (activeSession ? isEnding : isStarting)}
        >
          {isLoading
            ? 'Загрузка…'
            : activeSession
              ? isEnding
                ? 'Завершение…'
                : 'Завершить смену'
              : isStarting
                ? 'Начало…'
                : 'Начать смену'}
        </StatusActionButton>

        {activeSession && (
          <div className={styles.collapsedSecondaryActions}>
            <StatusActionButton
              variant="primary"
              fullWidth
              icon="ri-add-circle-line"
              onClick={handleAddCheckpoint}
              disabled={isAddingCheckpoint}
            >
              {isAddingCheckpoint ? 'Добавление…' : 'Добавить контрольную точку'}
            </StatusActionButton>
            <StatusActionButton
              variant="secondary"
              fullWidth
              icon="ri-time-line"
              onClick={() => setShowAddActivitySheet(true)}
              disabled={isAddingActivity}
            >
              Добавить активность
            </StatusActionButton>
          </div>
        )}

        {!isLoading && activeSession && hasUnsetActiveActivities && (
          <div className={styles.warningBanner}>
            <span className={styles.warningText}>
              <i className="ri-alert-line" aria-hidden />
              Укажите виды работ (
              {activeSessionActivities.filter((a) => !a.typeOfWorkId || a.typeOfWorkId <= 1).length})
            </span>
            <StatusActionButton
              variant="warning"
              fullWidth
              icon="ri-edit-line"
              onClick={() => handleOpenActivitiesSheet(activeSession)}
            >
              Указать виды работ
            </StatusActionButton>
          </div>
        )}

        {!isLoading && !activeSession && closedSessionsWithoutActivities.length > 0 && (
          <div className={styles.warningBanner}>
            <span className={styles.warningText}>
              <i className="ri-alert-line" aria-hidden />
              Требуется указать виды работ в {closedSessionsWithoutActivities.length}{' '}
              {getSessionWordForm(closedSessionsWithoutActivities.length)}
            </span>
            {closedSessionsWithoutActivities.slice(0, 2).map((session) => (
              <button
                key={session.id}
                type="button"
                className={styles.closedSessionItem}
                onClick={() => handleOpenActivitiesSheet(session)}
              >
                <span>
                  {new Date(session.timeStart).toLocaleString('ru-RU', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                  {session.timeEnd
                    ? ` – ${new Date(session.timeEnd).toLocaleTimeString('ru-RU', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}`
                    : ''}
                </span>
                {session.timeEnd && (
                  <span className={styles.closedSessionDuration}>
                    {(() => {
                      const start = new Date(session.timeStart);
                      const end = new Date(session.timeEnd);
                      const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
                      const hoursInt = Math.floor(hours);
                      const minutes = Math.round((hours - hoursInt) * 60);
                      return `${hoursInt} ч${minutes > 0 ? ` ${minutes} м` : ''}`;
                    })()}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <DashboardCard
        id="my-shift-v2-card"
        title="Моя смена"
        previewOnly
        collapsedPreviewVariant="shift"
        collapsedPreview={renderCollapsedPreview()}
      >
        {null}
      </DashboardCard>

      {/* Bottom Sheet для заполнения активностей закрытой смены */}
      <BottomSheet
        isOpen={showActivitiesSheet}
        onClose={handleCloseActivitiesSheet}
        title="Указать виды работ"
        size="large"
        showHandle={true}
        allowSwipeToClose={true}
        allowSwipeToExpand={true}
      >
        {sessionForActivities && (() => {
          const sessionStart = parseSessionTimeAsUtc(sessionForActivities.timeStart);
          const sessionEnd = timeEnd ? new Date(timeEnd) : (sessionForActivities.timeEnd ? parseSessionTimeAsUtc(sessionForActivities.timeEnd) : new Date());
          
          // Форматирование длительности для Timeline
          const formatDurationDisplay = (hours: number): string => {
            const h = Math.floor(hours);
            const m = Math.round((hours - h) * 60);
            if (m === 0) {
              return `${h}ч`;
            }
            return `${h}ч ${m}м`;
          };

          return (
            <div>
              {/* Секция активностей */}
              <div className="activities-section-wrapper">
                <div className="activities-section-header">
                  <div className="activities-section-title-container">
                    <div className="section-title">
                      <h3>Активности</h3>
                    </div>
                  </div>
                  <div className="activities-section-divider"></div>
                </div>
                <div className="activities-section-actions">
                  <button
                    type="button"
                    className="btn-add-row"
                    onClick={handleAddActivity}
                  >
                    <i className="ri-add-line"></i>
                    <span>Добавить активности</span>
                  </button>
                </div>
                <div className="activities-section">
                  {/* Временная шкала с ползунками */}
                  {activities.length >= 1 && (() => {
                    // Преобразуем сегменты в формат Timeline
                    const timelineSegmentsData: TimelineSegment[] = timelineSegments.map((segment) => {
                      const workTypeName = segment.activity.typeOfWorkName || 
                        typesOfWork.find(t => t.id === segment.activity.typeOfWorkId)?.name || 
                        'Не выбрано';
                      
                      return {
                        id: segment.activity.id,
                        start: segment.start,
                        end: segment.end,
                        label: workTypeName,
                        duration: segment.duration !== undefined 
                          ? segment.duration 
                          : segment.activity.hours,
                        data: segment.activity
                      };
                    });

                    // Преобразуем точки перетаскивания в формат Timeline
                    const timelineHandles: TimelineHandle[] = intersectionTimes.map((time, index) => ({
                      id: `handle-${index}`,
                      time
                    }));

                    return (
                      <Timeline
                        startTime={sessionStart}
                        endTime={sessionEnd}
                        segments={timelineSegmentsData}
                        handles={timelineHandles}
                        onHandleDrag={(handleId, newTime) => {
                          const handleIndex = parseInt(handleId.toString().replace('handle-', ''));
                          if (!isNaN(handleIndex) && sortedActivities.length > handleIndex + 1) {
                            const nextActivity = sortedActivities[handleIndex + 1];
                            setActivities(prev => {
                              const index = prev.findIndex(a => a.id === nextActivity.id);
                              if (index === -1) return prev;
                              
                              const updated = [...prev];
                              updated[index] = {
                                ...updated[index],
                                timeStart: newTime,
                                isModified: true
                              };
                              const currentStart = parseSessionTimeAsUtc(sessionForActivities.timeStart);
                              const currentEnd = timeEnd ? new Date(timeEnd) : (sessionForActivities.timeEnd ? parseSessionTimeAsUtc(sessionForActivities.timeEnd) : new Date());
                              return recalculateStats(updated, currentStart, currentEnd);
                            });
                          }
                        }}
                        onHandleDragEnd={(handleId, newTime) => {
                          // Изменения уже применены в onHandleDrag
                        }}
                        formatTime={formatTime}
                        formatDuration={formatDurationDisplay}
                        minSegmentWidth={8}
                        showTimeLabels={true}
                        showDuration={true}
                        showLabels={true}
                        timelineRef={setTimelineRef}
                        className="activities-timeline"
                      />
                    );
                  })()}

                  <div className="activities-table">
                    <div className="table-body">
                      {activities.length > 0 && (
                        <>
                          {/* Разделитель и время начала первой активности */}
                          <div className="activity-separator">
                            <div className="activity-separator-line"></div>
                            <div className="activity-separator-time">
                              <span title="Начало смены" className="activity-start-time-label">
                                {formatTime(activities[0].timeStart)}
                              </span>
                            </div>
                          </div>
                          {activities.map((item, index) => (
                            <ActivityItemComponent
                              key={item.id}
                              item={item}
                              index={index}
                              activities={activities}
                              sessionEnd={sessionEnd}
                              formatTime={formatTime}
                              formatDuration={formatDuration}
                              getActivityEndTime={getActivityEndTime}
                              updateActivity={updateActivity}
                              handleDeleteActivity={handleDeleteActivity}
                              onCommentClick={handleCommentClick}
                              onAdditionalWorkClick={handleAdditionalWorkClick}
                              onActivityTypeClick={handleOpenActivityTypeEditor}
                              isLast={index === activities.length - 1}
                            />
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="create-session-form-content">
                <div className="form-actions">
                  <StatusActionButton
                    type="button"
                    variant="secondary"
                    onClick={handleCloseActivitiesSheet}
                    disabled={isEnding}
                  >
                    Отмена
                  </StatusActionButton>
                  <StatusActionButton
                    type="button"
                    variant="primary"
                    onClick={handleSaveActivities}
                    loading={isEnding}
                    disabled={activities.length === 0}
                    icon="ri-save-line"
                  >
                    Сохранить активности
                  </StatusActionButton>
                </div>
              </div>
            </div>
          );
        })()}
      </BottomSheet>

      {/* Окно комментария к активности (в «Указать виды работ») */}
      <BottomSheet
        isOpen={showCommentSheet}
        onClose={handleCloseCommentSheet}
        title={currentActivityForComment?.comment ? 'Редактировать комментарий' : 'Комментарий к активности'}
        size="medium"
        showHandle={true}
        allowSwipeToClose={true}
      >
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', color: '#e0e0e0', fontSize: '14px', fontWeight: '500' }}>Комментарий</label>
            <textarea
              value={activityComment}
              onChange={(e) => setActivityComment(e.target.value)}
              placeholder="Введите комментарий..."
              rows={4}
              style={{
                width: '100%', padding: '12px', backgroundColor: '#2a2a2a', color: '#e0e0e0',
                border: '1px solid #444', borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit',
                resize: 'vertical', minHeight: '100px'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', color: '#e0e0e0', fontSize: '14px', fontWeight: '500' }}>Фотографии (необязательно)</label>
            <input ref={commentFileInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleCommentPhotoSelect} />
            {commentPhotoPreviews.length < 10 && (
              <button type="button" onClick={() => openFileInput(commentFileInputRef.current)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', width: '100%', backgroundColor: 'rgba(255,255,255,0.06)', border: '1px dashed #555', borderRadius: '8px', color: '#888', fontSize: '14px', cursor: 'pointer' }}>
                <i className="ri-image-add-line" style={{ fontSize: '20px' }} /> Прикрепить фото ({commentPhotoPreviews.length}/10)
              </button>
            )}
            {commentPhotoPreviews.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                {commentPhotoPreviews.map((url, i) => (
                  <div key={i} style={{ position: 'relative', width: '72px', height: '72px', flexShrink: 0 }}>
                    <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', backgroundColor: '#2a2a2a' }} />
                    <button type="button" onClick={() => handleRemoveCommentPhoto(i)} aria-label="Удалить фото" style={{ position: 'absolute', top: '4px', right: '4px', width: '24px', height: '24px', borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.7)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>
                      <i className="ri-close-line" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <StatusActionButton type="button" variant="secondary" onClick={handleCloseCommentSheet} fullWidth>Отмена</StatusActionButton>
            <StatusActionButton type="button" variant="primary" onClick={handleSaveComment} fullWidth>Сохранить</StatusActionButton>
          </div>
        </div>
      </BottomSheet>

      {/* Окно пометки «доп.работа» (в «Указать виды работ») */}
      <BottomSheet
        isOpen={showAdditionalWorkSheet}
        onClose={handleCloseAdditionalWorkSheet}
        title={currentActivityForAdditionalWork?.isAdditionalWork ? 'Доп.работа' : 'Пометить как доп.работу'}
        size="medium"
        showHandle={true}
        allowSwipeToClose={true}
      >
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{
            padding: '12px',
            backgroundColor: currentActivityForAdditionalWork?.isAdditionalWork ? 'rgba(255, 170, 0, 0.1)' : 'rgba(136, 136, 136, 0.1)',
            borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '12px'
          }}>
            <i
              className={currentActivityForAdditionalWork?.isAdditionalWork ? 'ri-star-fill' : 'ri-star-line'}
              style={{ fontSize: '24px', color: currentActivityForAdditionalWork?.isAdditionalWork ? '#ffaa00' : '#888' }}
            />
            <div>
              <div style={{ fontWeight: '500', color: '#e0e0e0' }}>
                {currentActivityForAdditionalWork?.isAdditionalWork ? 'Эта активность помечена как доп.работа' : 'Эта активность не помечена как доп.работа'}
              </div>
              <div style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>
                {currentActivityForAdditionalWork?.typeOfWorkName || 'Вид работы не указан'}
              </div>
            </div>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', color: '#e0e0e0', fontSize: '14px', fontWeight: '500' }}>
              {currentActivityForAdditionalWork?.isAdditionalWork ? 'Причина допа (можно редактировать)' : 'Причина (необязательно)'}
            </label>
            <textarea
              value={additionalWorkReason}
              onChange={(e) => setAdditionalWorkReason(e.target.value)}
              placeholder="Опишите доп.работу..."
              rows={4}
              style={{
                width: '100%', padding: '12px', backgroundColor: '#2a2a2a', color: '#e0e0e0',
                border: '1px solid #444', borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit',
                resize: 'vertical', minHeight: '100px'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', color: '#e0e0e0', fontSize: '14px', fontWeight: '500' }}>Фотографии (необязательно)</label>
            <input ref={additionalWorkFileInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleAdditionalWorkPhotoSelect} />
            {additionalWorkPhotoPreviews.length < 10 && (
              <button type="button" onClick={() => openFileInput(additionalWorkFileInputRef.current)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', width: '100%', backgroundColor: 'rgba(255,255,255,0.06)', border: '1px dashed #555', borderRadius: '8px', color: '#888', fontSize: '14px', cursor: 'pointer' }}>
                <i className="ri-image-add-line" style={{ fontSize: '20px' }} /> Прикрепить фото ({additionalWorkPhotoPreviews.length}/10)
              </button>
            )}
            {additionalWorkPhotoPreviews.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                {additionalWorkPhotoPreviews.map((url, i) => (
                  <div key={i} style={{ position: 'relative', width: '72px', height: '72px', flexShrink: 0 }}>
                    <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', backgroundColor: '#2a2a2a' }} />
                    <button type="button" onClick={() => handleRemoveAdditionalWorkPhoto(i)} aria-label="Удалить фото" style={{ position: 'absolute', top: '4px', right: '4px', width: '24px', height: '24px', borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.7)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>
                      <i className="ri-close-line" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {currentActivityForAdditionalWork?.isAdditionalWork ? (
            <div style={{ display: 'flex', gap: '12px' }}>
              <StatusActionButton type="button" variant="secondary" onClick={handleCloseAdditionalWorkSheet} fullWidth>Закрыть</StatusActionButton>
              <StatusActionButton type="button" variant="danger" onClick={handleRemoveAdditionalWorkMark} fullWidth>Удалить пометку доп</StatusActionButton>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '12px' }}>
              <StatusActionButton type="button" variant="secondary" onClick={handleCloseAdditionalWorkSheet} fullWidth>Отмена</StatusActionButton>
              <StatusActionButton type="button" variant="primary" onClick={handleToggleAdditionalWork} fullWidth>Пометить как доп.работу</StatusActionButton>
            </div>
          )}
        </div>
      </BottomSheet>

      {/* Bottom sheet для добавления активности с длительностью */}
      <BottomSheet
        isOpen={showAddActivitySheet}
        onClose={() => {
          setShowAddActivitySheet(false);
          setActivityHours(1);
          setActivityMinutes(0);
        }}
        title="Добавить активность"
        size="medium"
        showHandle={true}
        allowSwipeToClose={true}
      >
        <div style={{ padding: '20px' }}>
          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontSize: '14px', 
              fontWeight: '500',
              color: '#e0e0e0'
            }}>
              Длительность активности
            </label>
            
            <div style={{ 
              display: 'flex', 
              gap: '16px', 
              alignItems: 'center' 
            }}>
              {/* Часы */}
              <div style={{ flex: 1 }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '6px', 
                  fontSize: '12px', 
                  color: '#888'
                }}>
                  Часы
                </label>
                <input
                  type="number"
                  min="0"
                  max="23"
                  value={activityHours}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 0;
                    setActivityHours(Math.max(0, Math.min(23, value)));
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#2a2a2a',
                    border: '1px solid #444',
                    borderRadius: '8px',
                    color: '#e0e0e0',
                    fontSize: '16px',
                    outline: 'none'
                  }}
                />
              </div>
              
              {/* Минуты */}
              <div style={{ flex: 1 }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '6px', 
                  fontSize: '12px', 
                  color: '#888'
                }}>
                  Минуты
                </label>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={activityMinutes}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 0;
                    setActivityMinutes(Math.max(0, Math.min(59, value)));
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#2a2a2a',
                    border: '1px solid #444',
                    borderRadius: '8px',
                    color: '#e0e0e0',
                    fontSize: '16px',
                    outline: 'none'
                  }}
                />
              </div>
            </div>
            
            <div style={{ 
              marginTop: '12px', 
              padding: '12px', 
              backgroundColor: '#1a1a1a', 
              borderRadius: '8px',
              fontSize: '13px',
              color: '#888'
            }}>
              Активность будет добавлена в прошлое на указанный период времени
            </div>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '12px' 
          }}>
            <StatusActionButton
              type="button"
              variant="secondary"
              onClick={() => {
                setShowAddActivitySheet(false);
                setActivityHours(1);
                setActivityMinutes(0);
              }}
              disabled={isAddingActivity}
              fullWidth
            >
              Отмена
            </StatusActionButton>
            
            <StatusActionButton
              type="button"
              variant="primary"
              onClick={handleAddActivityWithDuration}
              loading={isAddingActivity}
              disabled={isAddingActivity || (activityHours === 0 && activityMinutes === 0)}
              fullWidth
              icon="ri-add-line"
            >
              Добавить
            </StatusActionButton>
          </div>
        </div>
      </BottomSheet>

      {/* Модальное окно подтверждения удаления пересекающихся активностей */}
      <BottomSheet
        isOpen={showConfirmDeleteSheet}
        onClose={() => {
          setShowConfirmDeleteSheet(false);
          setActivitiesToDelete([]);
          setPendingActivityData(null);
          setShowAddActivitySheet(true);
        }}
        title="Подтверждение удаления"
        size="medium"
        showHandle={true}
        allowSwipeToClose={false}
      >
        <div style={{ padding: '20px' }}>
          <div style={{ marginBottom: '24px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px',
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#2a1a0a',
              border: '1px solid #ff8800',
              borderRadius: '8px'
            }}>
              <i className="ri-alert-line" style={{ fontSize: '24px', color: '#ff8800' }}></i>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', color: '#ff8800', marginBottom: '4px' }}>
                  Внимание!
                </div>
                <div style={{ fontSize: '14px', color: '#ccc' }}>
                  Добавляемая активность пересекается с существующими активностями
                </div>
              </div>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '14px', color: '#888', marginBottom: '8px' }}>
                Будет удалено активностей: <strong style={{ color: '#e0e0e0' }}>{activitiesToDelete.length}</strong>
              </div>
              
              {activitiesToDelete.length > 0 && (
                <div style={{ 
                  maxHeight: '200px', 
                  overflowY: 'auto',
                  backgroundColor: '#1a1a1a',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  {activitiesToDelete.map((activity, index) => {
                    const hours = activity.hours ?? activity.allocatedHours ?? 0;
                    const timeDisplay = activity.timeStart
                      ? (activity.timeEnd
                          ? `${new Date(activity.timeStart).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })} - ${new Date(activity.timeEnd).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`
                          : `${new Date(activity.timeStart).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })} - текущее время`)
                      : null;
                    
                    return (
                      <div key={activity.id} style={{ 
                        padding: '8px 0',
                        borderBottom: index < activitiesToDelete.length - 1 ? '1px solid #333' : 'none'
                      }}>
                        <div style={{ fontSize: '13px', color: '#e0e0e0' }}>
                          {activity.typeOfWorkName || 'Не указано'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                          {timeDisplay ? `${timeDisplay} (${hours.toFixed(1)} ч)` : `${hours.toFixed(1)} ч`}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            <div style={{ 
              padding: '12px', 
              backgroundColor: '#1a1a1a', 
              borderRadius: '8px',
              fontSize: '13px',
              color: '#888'
            }}>
              Вы уверены, что хотите удалить эти активности? Добавленная активность заменит их.
            </div>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '12px' 
          }}>
            <StatusActionButton
              type="button"
              variant="secondary"
              onClick={() => {
                setShowConfirmDeleteSheet(false);
                setActivitiesToDelete([]);
                setPendingActivityData(null);
                setShowAddActivitySheet(true);
              }}
              disabled={isAddingActivity}
              fullWidth
            >
              Отмена
            </StatusActionButton>
            
            <StatusActionButton
              type="button"
              variant="danger"
              onClick={handleConfirmDelete}
              loading={isAddingActivity}
              disabled={isAddingActivity}
              fullWidth
              icon="ri-delete-bin-line"
            >
              Удалить и добавить
            </StatusActionButton>
          </div>
        </div>
      </BottomSheet>

      {/* Модальное окно выбора активностей: Ставка | Сделка */}
      <BottomSheet
        isOpen={showWorkTypeSelector}
        onClose={handleCloseWorkTypeSelector}
        title={editingActivityId !== null ? 'Изменить тип активности' : 'Выберите активности'}
        size="large"
        showHandle={true}
        allowSwipeToClose={true}
        allowSwipeToExpand={true}
      >
        <div className="work-type-selector-content">
          <div className="activity-source-tabs-wrapper">
            <HorizontalCategoryTabs
              categories={['Ставка', 'Сделка']}
              value={activitySourceTab === 'ставка' ? 'Ставка' : 'Сделка'}
              onChange={(v) => setActivitySourceTab(v === 'Сделка' ? 'сделка' : 'ставка')}
              showAllOption={false}
              size="medium"
              className="activity-source-tabs"
            />
          </div>
          {activitySourceTab === 'ставка' && (
            <>
              <div className="work-type-search">
                <div className="search-input-wrapper">
                  <i className="ri-search-line search-icon"></i>
                  <input
                    type="text"
                    placeholder="Поиск по названию или категории..."
                    value={workTypeSearchQuery}
                    onChange={(e) => setWorkTypeSearchQuery(e.target.value)}
                    className="search-input"
                  />
                </div>
              </div>
              {(() => {
                const categories = Array.from(new Set(typesOfWork.map(type => type.section)));
                return categories.length > 0 && (
                  <HorizontalCategoryTabs
                    categories={categories}
                    value={selectedWorkTypeCategory}
                    onChange={setSelectedWorkTypeCategory}
                    showAllOption={true}
                    allOptionLabel="Все категории"
                    size="medium"
                  />
                );
              })()}
              <div className="work-type-list">
                {loadingWorkTypes ? (
                  <div className="work-type-loading">
                    <i className="ri-loader-4-line ri-spin"></i>
                    <span>Загрузка типов работ...</span>
                  </div>
                ) : Object.keys(groupedTypes).length === 0 ? (
                  <div className="work-type-empty">
                    <i className="ri-search-line"></i>
                    <span>Типы работ не найдены</span>
                  </div>
                ) : (
                  Object.entries(groupedTypes).map(([category, types]) => (
                    <div key={category} className="work-type-category-group">
                      <div className="category-header">
                        <h4>{category}</h4>
                        <span className="category-count">{types.length}</span>
                      </div>
                      <div className="work-type-grid">
                        {types.map(type => {
                          const isSelected = selectedWorkTypeIds.includes(type.id);
                          return (
                            <div
                              key={type.id}
                              className={`work-type-card ${isSelected ? 'selected' : ''}`}
                              onClick={() => toggleWorkTypeSelection(type.id)}
                            >
                              <div className="work-type-card-content">
                                <div className="work-type-card-name">{type.name}</div>
                              </div>
                              {isSelected && (
                                <div className="work-type-card-check">
                                  <i className="ri-check-line"></i>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
          {activitySourceTab === 'сделка' && (
            <>
              <div className="work-type-search">
                <div className="search-input-wrapper">
                  <i className="ri-search-line search-icon"></i>
                  <input
                    type="text"
                    placeholder="Поиск по названию контракта..."
                    value={contractSearchQuery}
                    onChange={(e) => setContractSearchQuery(e.target.value)}
                    className="search-input"
                  />
                </div>
              </div>
              <div className="work-type-list">
                {loadingContracts ? (
                  <div className="work-type-loading">
                    <i className="ri-loader-4-line ri-spin"></i>
                    <span>Загрузка контрактов...</span>
                  </div>
                ) : (() => {
                  const filteredContracts = contracts.filter(c =>
                    c.title.toLowerCase().includes(contractSearchQuery.toLowerCase()) ||
                    (c.description || '').toLowerCase().includes(contractSearchQuery.toLowerCase())
                  );
                  return filteredContracts.length === 0 ? (
                    <div className="work-type-empty">
                      <i className="ri-file-list-3-line"></i>
                      <span>Контракты не найдены</span>
                      <small>Создайте контракт в разделе «Контракты» объекта</small>
                    </div>
                  ) : (
                    <div className="work-type-grid contract-grid">
                      {filteredContracts.map(contract => {
                        const isSelected = selectedContractIds.includes(contract.id);
                        return (
                          <div
                            key={contract.id}
                            className={`work-type-card ${isSelected ? 'selected' : ''}`}
                            onClick={() => toggleContractSelection(contract.id)}
                          >
                            <div className="work-type-card-content">
                              <div className="work-type-card-name">{contract.title}</div>
                              {contract.description && (
                                <div className="work-type-card-subtitle">{contract.description}</div>
                              )}
                              <div className="work-type-card-subtitle">
                                {contract.fixedAmount.toLocaleString('ru-RU')} ₽
                              </div>
                            </div>
                            {isSelected && (
                              <div className="work-type-card-check">
                                <i className="ri-check-line"></i>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </>
          )}
          <div className="work-type-actions">
            <StatusActionButton
              type="button"
              variant="secondary"
              onClick={handleCloseWorkTypeSelector}
            >
              Отмена
            </StatusActionButton>
            <StatusActionButton
              type="button"
              variant="primary"
              onClick={handleConfirmWorkTypeSelection}
              disabled={(activitySourceTab === 'ставка' ? selectedWorkTypeIds.length : selectedContractIds.length) === 0}
            >
              {editingActivityId !== null
                ? 'Применить'
                : `Добавить (${activitySourceTab === 'ставка' ? selectedWorkTypeIds.length : selectedContractIds.length})`}
            </StatusActionButton>
          </div>
        </div>
      </BottomSheet>

      {/* Окно подтверждения начала смены */}
      <BottomSheet
        isOpen={showConfirmStartShift}
        onClose={() => setShowConfirmStartShift(false)}
        title="Подтверждение начала смены"
        size="medium"
        showHandle={true}
        allowSwipeToClose={true}
      >
        <div style={{ padding: '20px' }}>
          <div style={{ marginBottom: '24px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px',
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#1a1a2a',
              border: '1px solid #4a90e2',
              borderRadius: '8px'
            }}>
              <i className="ri-information-line" style={{ fontSize: '24px', color: '#4a90e2' }}></i>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', color: '#4a90e2', marginBottom: '4px' }}>
                  Подтверждение
                </div>
                <div style={{ fontSize: '14px', color: '#ccc' }}>
                  Вы уверены, что хотите начать новую смену?
                </div>
              </div>
            </div>
            
            <div style={{ 
              padding: '12px', 
              backgroundColor: '#1a1a1a', 
              borderRadius: '8px',
              fontSize: '13px',
              color: '#888'
            }}>
              Смена будет начата с текущего времени. После начала смены вы сможете добавлять контрольные точки и активности.
            </div>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '12px' 
          }}>
            <StatusActionButton
              type="button"
              variant="secondary"
              onClick={() => setShowConfirmStartShift(false)}
              disabled={isStarting}
              fullWidth
            >
              Отмена
            </StatusActionButton>
            
            <StatusActionButton
              type="button"
              variant="primary"
              onClick={performStartShift}
              loading={isStarting}
              disabled={isStarting}
              fullWidth
              icon="ri-play-circle-line"
            >
              Начать смену
            </StatusActionButton>
          </div>
        </div>
      </BottomSheet>

      {/* Окно подтверждения завершения смены */}
      <BottomSheet
        isOpen={showConfirmEndShift}
        onClose={() => setShowConfirmEndShift(false)}
        title="Подтверждение завершения смены"
        size="medium"
        showHandle={true}
        allowSwipeToClose={true}
      >
        <div style={{ padding: '20px' }}>
          <div style={{ marginBottom: '24px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px',
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#2a1a1a',
              border: '1px solid #e74c3c',
              borderRadius: '8px'
            }}>
              <i className="ri-alert-line" style={{ fontSize: '24px', color: '#e74c3c' }}></i>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', color: '#e74c3c', marginBottom: '4px' }}>
                  Внимание!
                </div>
                <div style={{ fontSize: '14px', color: '#ccc' }}>
                  Вы уверены, что хотите завершить текущую смену?
                </div>
              </div>
            </div>
            
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '12px' 
          }}>
            <StatusActionButton
              type="button"
              variant="secondary"
              onClick={() => setShowConfirmEndShift(false)}
              disabled={isEnding}
              fullWidth
            >
              Отмена
            </StatusActionButton>
            
            <StatusActionButton
              type="button"
              variant="danger"
              onClick={performEndShift}
              loading={isEnding}
              disabled={isEnding}
              fullWidth
              icon="ri-stop-circle-line"
            >
              Завершить смену
            </StatusActionButton>
          </div>
        </div>
      </BottomSheet>

      {/* Окно подтверждения добавления контрольной точки */}
      <BottomSheet
        isOpen={showConfirmAddCheckpoint}
        onClose={() => setShowConfirmAddCheckpoint(false)}
        title="Подтверждение добавления контрольной точки"
        size="medium"
        showHandle={true}
        allowSwipeToClose={true}
      >
        <div style={{ padding: '20px' }}>
          <div style={{ marginBottom: '24px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px',
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#1a1a2a',
              border: '1px solid #4a90e2',
              borderRadius: '8px'
            }}>
              <i className="ri-information-line" style={{ fontSize: '24px', color: '#4a90e2' }}></i>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', color: '#4a90e2', marginBottom: '4px' }}>
                  Подтверждение
                </div>
                <div style={{ fontSize: '14px', color: '#ccc' }}>
                  Вы уверены, что хотите добавить контрольную точку?
                </div>
              </div>
            </div>
            
            <div style={{ 
              padding: '12px', 
              backgroundColor: '#1a1a1a', 
              borderRadius: '8px',
              fontSize: '13px',
              color: '#888'
            }}>
              Контрольная точка будет добавлена в текущее время. Текущая активная активность будет закрыта, и будет создана новая активная активность с видом работы "Не указано".
            </div>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '12px' 
          }}>
            <StatusActionButton
              type="button"
              variant="secondary"
              onClick={() => setShowConfirmAddCheckpoint(false)}
              disabled={isAddingCheckpoint}
              fullWidth
            >
              Отмена
            </StatusActionButton>
            
            <StatusActionButton
              type="button"
              variant="primary"
              onClick={performAddCheckpoint}
              loading={isAddingCheckpoint}
              disabled={isAddingCheckpoint}
              fullWidth
              icon="ri-add-circle-line"
            >
              Добавить контрольную точку
            </StatusActionButton>
          </div>
        </div>
      </BottomSheet>
    </>
  );
};

export default MyShiftCardV2;

